import {
  DialogClose,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  toast,
} from '@qodo/design-system';
import { useChangePlanMutation } from 'network/mutations';
import { formatUserFriendlyDate } from 'utils/date';
import { formatNumber } from 'utils/number';
import { getServerErrorDetail } from 'utils/serverError';
import { spawn } from 'utils/translations';
import { DowngradeConfirmationDialogProps } from './types';

const spawnDialog = spawn('dialogs.downgrade-confirmation');
const spawnDialogActions = spawn('dialogs.downgrade-confirmation.action');

/**
 * Confirmation dialog for scheduling a self-serve downgrade to a smaller credit
 * pack at period end. There's no immediate charge — the user keeps
 * their current pool until effectiveAt. On confirm the change-plan mutation fires
 * and flips the cached state to scheduledToChange so the UI updates.
 *
 * When the current subscription is scheduled to cancel, the downgrade first voids
 * that cancellation, so the secondary label becomes "Keep cancellation" and a
 * re-activation note is shown.
 */
export const DowngradeConfirmationDialog = ({
  target,
  isScheduledToCancel = false,
  onConfirm,
  ...props
}: DowngradeConfirmationDialogProps) => {
  const { onOpenChange = () => {} } = props;
  const { mutate: changePlan, isPending } = useChangePlanMutation();

  const onSubmit = () => {
    // Guard re-entrancy: the button is loading-disabled while pending, but this
    // also blocks any non-UI re-invocation from firing a duplicate POST.
    if (!target || isPending) {
      return;
    }

    changePlan(
      {
        targetProductId: target.targetProductId,
        effectiveAt: target.effectiveAt,
      },
      {
        // Close only after the schedule succeeds — an error keeps the dialog
        // open so the user sees the toast and can retry, rather than dismissing
        // as if the downgrade went through.
        onSuccess: () => {
          toast.success(spawnDialogActions('success'));
          onOpenChange(false);
          onConfirm?.();
        },
        onError: (error) => {
          // The BE surfaces an actionable message in `detail` — notably that a
          // restored cancellation "remains intact" on a failed reactivate. Prefer
          // it over the generic copy when present.
          toast.error(
            getServerErrorDetail(error) || spawnDialogActions('error'),
          );
        },
      },
    );
  };

  const cancelLabel = isScheduledToCancel
    ? spawnDialogActions('cancel-scheduled')
    : spawnDialogActions('cancel');

  return (
    <Dialog {...props}>
      <DialogContent autoFocus={false} className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="text-h3">{spawnDialog('title')}</DialogTitle>
        </DialogHeader>
        <DialogDescription>
          {target
            ? spawnDialog('description', {
                credits: formatNumber(target.credits),
                date: formatUserFriendlyDate(target.effectiveAt),
              })
            : null}
        </DialogDescription>
        {isScheduledToCancel && (
          <p className="text-sm text-text-panel-neutral-mid">
            {spawnDialog('reactivation-note')}
          </p>
        )}
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="primary">{cancelLabel}</Button>
          </DialogClose>
          <Button variant="outline" onClick={onSubmit} loading={isPending}>
            {spawnDialogActions('confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
